import { useRef, useState } from "react";
import type {
  QuoteRequestAnswer,
  QuoteRequestPage,
} from "@shared-core/data/supplier-portal-adapter";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  QUOTE_REQUEST_WORDING,
  buildQuoteAnswer,
  inputFromPence,
  quantityLabel,
  submissionRequestIds,
  type QuotePriceFormRow,
} from "@shared-core/domain/supplier-quote-request";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { FilePicker } from "../../app/components/patterns/FilePicker";
import { FormField } from "../../app/components/patterns/FormField";

const PDF_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Your prices, delivered: a price for each quantity or Can't quote, when the prices hold, when the supplier could
 * despatch, their reference and, if they like, their own quote as a PDF. Prefilled from the page when changing
 * prices already sent. A refusal's field errors sit beside their inputs (`prices.N.cost` beside the price for that
 * row), with a summary above; the submission keeps one `request_id` however often it is retried.
 */
export function PricesForm({
  page,
  onSend,
  onCancel,
  onDecline,
}: {
  page: QuoteRequestPage;
  onSend: (answer: QuoteRequestAnswer, quoteDocument?: File) => Promise<void>;
  /** *Keep the prices we sent*, when changing prices already sent. */
  onCancel: (() => void) | null;
  /** *We can't quote for this…*, when the page allows it. */
  onDecline: (() => void) | null;
}) {
  const [rows, setRows] = useState<QuotePriceFormRow[]>(() =>
    page.quantities.map((q) => ({
      quantity: q.quantity,
      price: q.cost !== null ? inputFromPence(q.cost) : "",
      is_cant_quote: q.not_quoted,
    })),
  );
  const [despatchBy, setDespatchBy] = useState(page.despatch_by ?? "");
  const [validUntil, setValidUntil] = useState(page.valid_until ?? "");
  const [reference, setReference] = useState(page.your_reference ?? "");
  const [document, setDocument] = useState<File | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const write = useFieldErrorWrite(QUOTE_REQUEST_WORDING.errorFallback);
  const ids = useRef(submissionRequestIds());
  const errors = write.fieldErrors;

  const setRow = (n: number, change: Partial<QuotePriceFormRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === n ? { ...row, ...change } : row)),
    );

  const choose = (file: File | null) => {
    if (file && file.type !== "application/pdf") {
      setDocument(null);
      setDocumentError(QUOTE_REQUEST_WORDING.pdfOnly);
    } else if (file && file.size > PDF_MAX_BYTES) {
      setDocument(null);
      setDocumentError(QUOTE_REQUEST_WORDING.pdfTooLarge);
    } else {
      setDocument(file);
      setDocumentError(null);
    }
  };

  const submit = async () => {
    const answer = buildQuoteAnswer(
      {
        rows,
        despatch_by: despatchBy,
        valid_until: validUntil,
        your_reference: reference,
      },
      ids.current.current(),
    );
    const ok = await write.run(() => onSend(answer, document ?? undefined));
    if (ok) ids.current.renew();
  };

  return (
    <form
      noValidate
      aria-label="Your prices"
      className="min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Card className="gap-3 p-4">
        <p className="text-sm text-muted-foreground">
          {QUOTE_REQUEST_WORDING.pricesHint}
        </p>
        {write.banner && (
          <p role="alert" className="text-sm text-red-600">
            {write.banner}
          </p>
        )}
        <Table aria-describedby={errors.prices ? "prices-error" : undefined}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">
                {QUOTE_REQUEST_WORDING.quantity}
              </TableHead>
              <TableHead>{QUOTE_REQUEST_WORDING.yourPrice}</TableHead>
              <TableHead className="w-40">
                {QUOTE_REQUEST_WORDING.cantQuote}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, n) => {
              const label = quantityLabel(row.quantity);
              const error = errors[`prices.${n}.cost`];
              return (
                <TableRow
                  key={row.quantity}
                  className={cn(row.is_cant_quote && "text-muted-foreground")}
                >
                  <TableCell className="font-semibold tabular-nums">
                    {label}
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    <div className="grid w-44 gap-1">
                      <div className="relative">
                        <span
                          aria-hidden
                          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground"
                        >
                          £
                        </span>
                        <Input
                          inputMode="decimal"
                          className="pl-6 text-right tabular-nums"
                          aria-label={`Your price for ${label}`}
                          aria-invalid={error ? true : undefined}
                          aria-describedby={
                            error ? `price-${n}-error` : undefined
                          }
                          disabled={row.is_cant_quote}
                          value={row.price}
                          onChange={(event) =>
                            setRow(n, { price: event.target.value })
                          }
                        />
                      </div>
                      {error && (
                        <p
                          id={`price-${n}-error`}
                          className="text-sm text-red-600"
                        >
                          {error}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label={`Can't quote ${label}`}
                      checked={row.is_cant_quote}
                      onChange={(event) =>
                        setRow(
                          n,
                          event.target.checked
                            ? { is_cant_quote: true, price: "" }
                            : { is_cant_quote: false },
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {errors.prices && (
          <p id="prices-error" className="text-sm text-red-600">
            {errors.prices}
          </p>
        )}

        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
          <FormField
            id="quote-despatch-by"
            label={QUOTE_REQUEST_WORDING.despatchBy}
            error={errors.despatch_by}
          >
            {(control) => (
              <Input
                {...control}
                type="date"
                value={despatchBy}
                onChange={(event) => setDespatchBy(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            id="quote-valid-until"
            label={QUOTE_REQUEST_WORDING.validUntil}
            error={errors.valid_until}
          >
            {(control) => (
              <Input
                {...control}
                type="date"
                value={validUntil}
                onChange={(event) => setValidUntil(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            id="quote-reference"
            label={QUOTE_REQUEST_WORDING.yourReference}
            error={errors.their_reference}
          >
            {(control) => (
              <Input
                {...control}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            id="quote-document"
            label={QUOTE_REQUEST_WORDING.yourQuoteOptional}
            error={documentError ?? errors.quote_document}
          >
            {(control) => (
              <FilePicker
                {...control}
                compact
                file={document}
                onChange={choose}
                accept="application/pdf"
                prompt=""
                chooseLabel={QUOTE_REQUEST_WORDING.attach}
              />
            )}
          </FormField>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              {QUOTE_REQUEST_WORDING.cancelChange}
            </Button>
          )}
          {onDecline && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              onClick={onDecline}
            >
              {QUOTE_REQUEST_WORDING.cantQuoteForThis}
            </Button>
          )}
          <Button type="submit" disabled={write.isBusy}>
            {QUOTE_REQUEST_WORDING.sendPrices}
          </Button>
        </div>
      </Card>
    </form>
  );
}
